import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Ikonic Deals';
const SITE_URL = 'https://ikonicdeals.com';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_propmatch-7/artifacts/y6a3qiy4_LogotipoFinal_IkonicDeals%28web%29.jpg';

// SEO data for each page
export const SEO_DATA = {
  home: {
    title: 'Ikonic Deals | Bienes Raíces en Guatemala — Compra, Venta y Renta de Propiedades',
    description: 'Encuentra apartamentos, casas, terrenos y locales comerciales en Guatemala. Asesoría personalizada en compra, venta y renta de propiedades. Contacta a Ikonic Deals.',
    keywords: 'bienes raíces guatemala, inmobiliaria guatemala, casas en venta guatemala, apartamentos guatemala, terrenos guatemala, propiedades guatemala, compra venta propiedades'
  },
  properties: {
    title: 'Propiedades en Venta y Alquiler | Ikonic Deals Guatemala',
    description: 'Explora nuestro catálogo de propiedades exclusivas en Guatemala. Casas, apartamentos, terrenos y locales comerciales. Encuentra tu próxima inversión inmobiliaria.',
    keywords: 'propiedades en venta guatemala, casas en alquiler, apartamentos zona 10, terrenos en venta, locales comerciales guatemala'
  },
  about: {
    title: 'Nosotros | Ikonic Deals - Tu Inmobiliaria de Confianza en Guatemala',
    description: 'Conoce a Ikonic Deals, tu socio de confianza en el mercado inmobiliario guatemalteco. Más de 10 años de experiencia en compra, venta y renta de propiedades.',
    keywords: 'inmobiliaria guatemala, agente inmobiliario, asesoría inmobiliaria, ikonic deals'
  },
  sell: {
    title: 'Vende tu Propiedad | Ikonic Deals Guatemala',
    description: 'Vende tu casa, apartamento o terreno con Ikonic Deals. Marketing profesional, fotografía de alta calidad y acceso a compradores calificados en Guatemala.',
    keywords: 'vender casa guatemala, vender apartamento, vender terreno, inmobiliaria para vender'
  },
  contact: {
    title: 'Contacto | Ikonic Deals - Asesoría Inmobiliaria Guatemala',
    description: 'Contáctanos para asesoría personalizada en bienes raíces. Estamos en Zona 9, Guatemala. Llámanos al +502 4254-7204 o escríbenos por WhatsApp.',
    keywords: 'contacto inmobiliaria guatemala, asesor inmobiliario, whatsapp inmobiliaria'
  }
};

// Schema for RealEstateAgent (used on home page)
export const REAL_ESTATE_AGENT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  "name": "Ikonic Deals",
  "description": "Inmobiliaria líder en Guatemala especializada en compra, venta y renta de propiedades residenciales y comerciales.",
  "url": SITE_URL,
  "logo": DEFAULT_IMAGE,
  "image": DEFAULT_IMAGE,
  "telephone": "+502 4254-7204",
  "email": "hola@ikonicdeals.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Av. Reforma 9-00, Zona 9",
    "addressLocality": "Guatemala",
    "addressRegion": "Guatemala",
    "postalCode": "01009",
    "addressCountry": "GT"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "14.6349",
    "longitude": "-90.5069"
  },
  "openingHours": "Mo-Fr 09:00-18:00, Sa 09:00-13:00",
  "priceRange": "$$",
  "sameAs": [
    "https://www.facebook.com/ikonicdeals",
    "https://www.instagram.com/ikonicdeals"
  ],
  "areaServed": {
    "@type": "Country",
    "name": "Guatemala"
  }
};

// Generate schema for a property
export const generatePropertySchema = (property) => {
  const price = property.price_usd || property.price;
  const images = property.images?.map(img => img.url) || [];
  
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": property.name,
    "description": property.description,
    "url": `${SITE_URL}/propiedad/${property.slug || property.id}`,
    "image": images.length > 0 ? images : [DEFAULT_IMAGE],
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": property.address || "",
      "addressLocality": property.zone || "",
      "addressRegion": property.department || "Guatemala",
      "addressCountry": "GT"
    },
    "geo": property.latitude && property.longitude ? {
      "@type": "GeoCoordinates",
      "latitude": property.latitude,
      "longitude": property.longitude
    } : undefined,
    "numberOfRooms": property.bedrooms || undefined,
    "numberOfBathroomsTotal": property.bathrooms || undefined,
    "floorSize": property.area_m2 ? {
      "@type": "QuantitativeValue",
      "value": property.area_m2,
      "unitCode": "MTK"
    } : undefined
  };
};

// SEO Component
const SEO = ({ 
  title, 
  description, 
  keywords,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  schema,
  noindex = false
}) => {
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_GT" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* WhatsApp specific */}
      <meta property="og:image:alt" content={title} />
      
      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
